import { useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { Toaster, toast } from "sonner";
import { Trash2, LogOut } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { useLang } from "../contexts/LangContext";
import { api, formatApiError } from "../lib/api";
import AdminReviews from "../components/AdminReviews";
import useSEO from "../lib/useSEO";

const statusColors = {
  neu: "bg-[hsl(var(--primary))]/20 text-[hsl(var(--primary-foreground))] border-[hsl(var(--primary))]",
  in_bearbeitung: "bg-[hsl(var(--secondary))] text-[hsl(var(--foreground))] border-[hsl(var(--border))]",
  abgeschlossen: "bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))] border-[hsl(var(--border))]",
};

export default function AdminDashboard() {
  const { user, loading, logout } = useAuth();
  const { t } = useLang();
  const [items, setItems] = useState([]);
  const [fetching, setFetching] = useState(true);
  const [tab, setTab] = useState("inquiries");

  useSEO({ title: "Admin Dashboard | Tæste", noindex: true });

  const load = async () => {
    setFetching(true);
    try {
      const { data } = await api.get("/inquiries");
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setFetching(false);
    }
  };

  useEffect(() => {
    if (user && user.role === "admin") load();
  }, [user]);

  if (loading) return <div className="p-12">…</div>;
  if (!user || user.role !== "admin") return <Navigate to="/admin/login" replace />;

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/inquiries/${id}`, { status });
      setItems((arr) => arr.map((it) => (it.id === id ? { ...it, status } : it)));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Anfrage wirklich löschen?")) return;
    try {
      await api.delete(`/inquiries/${id}`);
      setItems((arr) => arr.filter((it) => it.id !== id));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[hsl(var(--background))]">
      <Toaster richColors position="top-center" />
      <div className="border-b border-border bg-[hsl(var(--card))]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-5 flex justify-between items-center">
          <Link to="/" className="brand-mark text-3xl">
            T<span className="italic">æ</span>ste
          </Link>
          <div className="flex items-center gap-4">
            <span className="text-sm text-[hsl(var(--muted-foreground))] hidden md:block">{user.email}</span>
            <button data-testid="admin-logout-button" onClick={logout} className="inline-flex items-center gap-2 px-4 py-2 text-xs tracking-widest uppercase font-semibold border border-border hover:bg-[hsl(var(--secondary))] rounded-sm">
              <LogOut size={14} /> {t.admin.logout}
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
          <h1 className="font-display text-4xl md:text-5xl">{tab === "reviews" ? "Rezensionen" : t.admin.dashboard_title}</h1>
          <nav className="flex gap-2" role="tablist" aria-label="Admin-Bereiche">
            <button
              type="button"
              role="tab"
              aria-selected={tab === "inquiries"}
              data-testid="admin-tab-inquiries"
              onClick={() => setTab("inquiries")}
              className={`px-4 py-2 text-xs tracking-[0.22em] uppercase font-semibold border rounded-sm transition-colors ${
                tab === "inquiries"
                  ? "bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] border-[hsl(var(--primary))]"
                  : "border-border hover:bg-[hsl(var(--secondary))]"
              }`}
            >
              Anfragen
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={tab === "reviews"}
              data-testid="admin-tab-reviews"
              onClick={() => setTab("reviews")}
              className={`px-4 py-2 text-xs tracking-[0.22em] uppercase font-semibold border rounded-sm transition-colors ${
                tab === "reviews"
                  ? "bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] border-[hsl(var(--primary))]"
                  : "border-border hover:bg-[hsl(var(--secondary))]"
              }`}
            >
              Rezensionen
            </button>
          </nav>
        </div>

        {tab === "reviews" ? (
          <AdminReviews />
        ) : fetching ? (
          <p>…</p>
        ) : items.length === 0 ? (
          <div data-testid="admin-empty" className="border border-border p-12 text-center text-[hsl(var(--muted-foreground))]">{t.admin.empty}</div>
        ) : (
          <div className="overflow-x-auto border border-border bg-[hsl(var(--card))]">
            <table className="w-full text-sm">
              <thead className="bg-[hsl(var(--secondary))]/60 text-left">
                <tr>
                  <th className="px-4 py-3 eyebrow">{t.admin.created}</th>
                  <th className="px-4 py-3 eyebrow">Name</th>
                  <th className="px-4 py-3 eyebrow">Kontakt</th>
                  <th className="px-4 py-3 eyebrow">Event</th>
                  <th className="px-4 py-3 eyebrow">Datum</th>
                  <th className="px-4 py-3 eyebrow">Gäste</th>
                  <th className="px-4 py-3 eyebrow">Kinder</th>
                  <th className="px-4 py-3 eyebrow">Konzept</th>
                  <th className="px-4 py-3 eyebrow">Nachricht</th>
                  <th className="px-4 py-3 eyebrow">Status</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {items.map((it) => (
                  <tr key={it.id} data-testid={`inquiry-row-${it.id}`} className="border-t border-border align-top">
                    <td className="px-4 py-3 whitespace-nowrap text-[hsl(var(--muted-foreground))]">
                      {new Date(it.created_at).toLocaleString("de-DE")}
                    </td>
                    <td className="px-4 py-3 font-medium">{it.name}</td>
                    <td className="px-4 py-3">
                      <div>{it.email}</div>
                      {it.phone && <div className="text-[hsl(var(--muted-foreground))]">{it.phone}</div>}
                    </td>
                    <td className="px-4 py-3">{it.event_type}</td>
                    <td className="px-4 py-3 whitespace-nowrap">{it.event_date || "—"}</td>
                    <td className="px-4 py-3">{it.guests || "—"}</td>
                    <td className="px-4 py-3">{it.children || "—"}</td>
                    <td className="px-4 py-3 max-w-[14rem]">
                      <span className="text-[hsl(var(--muted-foreground))]">{it.concept || "—"}</span>
                    </td>
                    <td className="px-4 py-3 max-w-xs">
                      <div className="text-[hsl(var(--muted-foreground))] line-clamp-3">{it.message || "—"}</div>
                      {it.location && <div className="text-xs mt-1">📍 {it.location}</div>}
                    </td>
                    <td className="px-4 py-3">
                      <select
                        data-testid={`status-select-${it.id}`}
                        value={it.status}
                        onChange={(e) => updateStatus(it.id, e.target.value)}
                        className={`px-2 py-1 text-xs uppercase tracking-widest font-semibold border rounded-sm ${statusColors[it.status]}`}
                      >
                        <option value="neu">{t.admin.status.neu}</option>
                        <option value="in_bearbeitung">{t.admin.status.in_bearbeitung}</option>
                        <option value="abgeschlossen">{t.admin.status.abgeschlossen}</option>
                      </select>
                    </td>
                    <td className="px-4 py-3">
                      <button data-testid={`delete-${it.id}`} onClick={() => remove(it.id)} className="p-2 hover:bg-[hsl(var(--destructive))]/10 text-[hsl(var(--destructive))] rounded-sm">
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
